import React, { useEffect, useMemo, useState } from "react";
import { selectUser } from "../../../features/userSlice";
import { useSelector } from "react-redux";
import {
  useTable,
  useGlobalFilter,
  useSortBy,
  usePagination,
} from "react-table";
import { Button, InputAdornment, TextField, Tooltip } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import AddIcon from "@mui/icons-material/Add";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DescriptionIcon from "@mui/icons-material/Description";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import { Link } from "react-router-dom";
import {
  getForms,
  getWindowFormDefinition,
  getWindowFormDefinitions,
} from "../../../components/Forms/api";
import { normalizeDate } from "../../../utilities";
import InteriorDoor from "../Door/InteriorDoor";
import { getDrafts } from "../../../components/Forms/api";
import ExteriorDoor from "../Door/ExteriorDoor";
import CustomWindowForm from "../WindowDoor/CustomWindowForm";
import { PageHeader, PageShell } from "../../../components/UI/Page";
import { EmptyState, LoadingState } from "../../../components/UI/States";

const formLabel = (r) => {
  if (r?.form_name) return r.form_name;
  switch (r?.form_id) {
    case 6:
      return "Interior Door";
    case 11:
      return "Exterior Door";
    default:
      return "RFQ Form";
  }
};

const ViewEntries = () => {
  const user = useSelector(selectUser);

  const [dataStore, setDataStore] = useState([]);
  const [formdataStore, setformDataStore] = useState([]);
  const [draftdataStore, setDraftDataStore] = useState([]);

  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState(null);
  const [modal, setModal] = useState(false);
  const [customFormDefinition, setCustomFormDefinition] = useState(null);
  const [windowFormDefinitions, setWindowFormDefinitions] = useState([]);

  const windowFormIdSet = React.useMemo(
    () =>
      new Set(
        (windowFormDefinitions || [])
          .map((definition) =>
            definition?.form_id !== undefined
              ? Number(definition.form_id)
              : null
          )
          .filter(Boolean)
      ),
    [windowFormDefinitions]
  );

  useEffect(() => {
    if (!user?.userID) return;
    getDrafts(setDraftDataStore, user);
    getForms(setformDataStore, user);
  }, [user]);

  useEffect(() => {
    getWindowFormDefinitions(setWindowFormDefinitions);
  }, []);

  // Tag each row with where it came from — form_entry rows are submitted,
  // form_drafts rows are still drafts. The table shows and filters on this.
  useEffect(() => {
    setDataStore([
      ...formdataStore.map((row) => ({ ...row, __status: "submitted" })),
      ...draftdataStore.map((row) => ({ ...row, __status: "draft" })),
    ]);
  }, [formdataStore, draftdataStore]);

  useEffect(() => {
    setLoading(false);
  }, [dataStore]);

  useEffect(() => {
    if (formData) {
      setModal(true);
    }
  }, [formData]);

  useEffect(() => {
    if (!formData || formData.form_id === 6 || formData.form_id === 11) {
      setCustomFormDefinition(null);
      return;
    }
    getWindowFormDefinition(setCustomFormDefinition, formData.form_id);
  }, [formData]);

  const deleteDraft = async (id) => {
    try {
      const rawResponse = await fetch("/api/forms/misc/getForms/delete", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ id }),
      });
      const result = await rawResponse.json();
      // Drop the row locally on success — no refetch round-trip needed.
      if (!result.error) {
        setDraftDataStore((prev) => prev.filter((row) => row._id !== id));
      }
    } catch (error) {
      // Leave the row in place; a refresh will reconcile.
    }
  };

  const closeDraft = () => {
    setModal(false);
    setFormData(null);
    setCustomFormDefinition(null);
    getDrafts(setDraftDataStore, user);
    getForms(setformDataStore, user);
  };

  const columns = useMemo(
    () => [
      {
        Header: "Entry Date",
        id: "entry_date",
        // Drafts only have last_save (set on every autosave); submitted
        // entries have entry_date. Falling back stops "Invalid Date".
        accessor: (row) => row.entry_date || row.last_save || null,
        sortType: (a, b, id) => {
          const x = new Date(a.values[id]).getTime() || 0;
          const y = new Date(b.values[id]).getTime() || 0;
          return x > y ? 1 : x < y ? -1 : 0;
        },
        Cell: ({ value }) => (value ? normalizeDate(new Date(value)) : "—"),
      },
      {
        Header: "Form",
        id: "form",
        accessor: (row) => formLabel(row),
      },
      {
        Header: "Status",
        accessor: "__status",
        Cell: ({ value }) =>
          value === "draft" ? (
            <span className="inline-flex whitespace-nowrap rounded-full bg-amber-500/15 px-2.5 py-0.5 text-xs font-semibold text-amber-700 dark:text-amber-300">
              DRAFT
            </span>
          ) : (
            <span className="inline-flex whitespace-nowrap rounded-full bg-emerald-500/15 px-2.5 py-0.5 text-xs font-semibold text-emerald-700 dark:text-emerald-300">
              SUBMITTED
            </span>
          ),
      },
      {
        Header: "Type",
        accessor: "form_type",
        Cell: ({ value }) =>
          value ? (
            <span className="inline-flex whitespace-nowrap rounded-full bg-slate-500/15 px-2.5 py-0.5 text-xs font-medium text-slate-700 dark:text-slate-300">
              {String(value).toUpperCase()}
            </span>
          ) : (
            "—"
          ),
      },
      {
        Header: "Customer",
        accessor: "customer",
        Cell: ({ value }) =>
          value || <span className="app-muted text-xs">—</span>,
      },
      {
        Header: "Job",
        accessor: "job",
        Cell: ({ value }) =>
          value || <span className="app-muted text-xs">—</span>,
      },
      {
        Header: "",
        id: "actions",
        disableSortBy: true,
        Cell: ({ row }) => {
          const rowData = row.original;
          const isDraft = rowData.__status === "draft";
          const isWindowView =
            !isDraft && windowFormIdSet.has(Number(rowData?.form_id));
          const isQuote =
            (rowData.form_type || "").toLowerCase() === "quote";

          // Drafts: pick up where you left off, or throw it away. The PDF
          // and window views only make sense once submitted.
          if (isDraft) {
            return (
              <div className="flex flex-wrap justify-end gap-2">
                <Button
                  onClick={() => setFormData(rowData)}
                  size="small"
                  variant="contained"
                >
                  Continue
                </Button>
                <Button
                  onClick={() => deleteDraft(rowData._id)}
                  size="small"
                  color="error"
                >
                  Delete
                </Button>
              </div>
            );
          }

          return (
            <div className="flex flex-wrap justify-end gap-2">
              {isWindowView ? (
                <Button
                  component={Link}
                  to={`/windowdoor/sales/windowforms/${rowData?._id}`}
                  size="small"
                  variant="outlined"
                >
                  View
                </Button>
              ) : (
                <Button
                  href={`/api/forms/pdf/?id=${rowData?._id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="small"
                  variant="outlined"
                  endIcon={<OpenInNewIcon sx={{ fontSize: 15 }} />}
                >
                  View PDF
                </Button>
              )}
              {isQuote && !isWindowView && (
                <Button
                  onClick={() => setFormData(rowData)}
                  size="small"
                  variant="contained"
                >
                  Edit Quote
                </Button>
              )}
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [windowFormIdSet]
  );

  const [statusFilter, setStatusFilter] = useState("all");

  const data = useMemo(() => {
    const rows = dataStore.slice().reverse();
    if (statusFilter === "all") return rows;
    return rows.filter((row) => row.__status === statusFilter);
  }, [dataStore, statusFilter]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    prepareRow,
    state,
    setGlobalFilter,
  } = useTable(
    {
      columns,
      data,
      initialState: {
        pageIndex: 0,
        pageSize: 10,
        sortBy: [{ id: "entry_date", desc: true }],
      },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const { globalFilter } = state;

  // While editing/viewing a draft, render that form full-screen.
  if (formData && modal) {
    return (
      // The forms bring their own centered container, so only the Close bar
      // sits in this one.
      <div className="w-full">
        <div className="mx-auto flex w-full max-w-6xl px-4 pt-6 sm:px-6">
          <Tooltip title="Back to My Forms">
            <Button
              onClick={closeDraft}
              variant="outlined"
              startIcon={<ArrowBackIcon />}
            >
              Close
            </Button>
          </Tooltip>
        </div>
        {formData.form_id === 6 && (
          <InteriorDoor formData={formData} setFormData={setFormData} />
        )}
        {formData.form_id === 11 && (
          <ExteriorDoor formData={formData} setFormData={setFormData} />
        )}
        {formData.form_id !== 6 && formData.form_id !== 11 && (
          <CustomWindowForm
            formData={formData}
            setFormData={setFormData}
            formDefinition={customFormDefinition}
            onClose={closeDraft}
          />
        )}
      </div>
    );
  }

  return (
    <PageShell size="xl">
      <PageHeader
        icon={DescriptionIcon}
        tone="sky"
        eyebrow="Forms"
        title="My Forms"
        description={
          loading
            ? "Your RFQ forms and drafts."
            : `${formdataStore.length} submitted · ${draftdataStore.length} draft${
                draftdataStore.length === 1 ? "" : "s"
              }`
        }
        actions={
          <Button
            component={Link}
            to="/forms/new/rfq"
            variant="contained"
            startIcon={<AddIcon />}
            className="whitespace-nowrap"
          >
            New RFQ Form
          </Button>
        }
      />

      {!loading && dataStore.length > 0 && (
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div
            className="flex flex-wrap gap-2"
            role="group"
            aria-label="Filter by status"
          >
            {[
              { key: "all", label: `All (${dataStore.length})` },
              { key: "draft", label: `Drafts (${draftdataStore.length})` },
              {
                key: "submitted",
                label: `Submitted (${formdataStore.length})`,
              },
            ].map((chip) => {
              const active = statusFilter === chip.key;
              return (
                <button
                  key={chip.key}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setStatusFilter(chip.key)}
                  className={`inline-flex min-h-[2.25rem] items-center rounded-full border px-3.5 text-sm font-medium tabular-nums transition focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 ${
                    active
                      ? "border-transparent bg-sky-600 text-white shadow-sm"
                      : "app-surface text-[color:var(--app-text)] hover:bg-[color:var(--app-hover)]"
                  }`}
                >
                  {chip.label}
                </button>
              );
            })}
          </div>
          {data.length > 0 && (
            <TextField
              size="small"
              placeholder="Search forms…"
              value={globalFilter || ""}
              onChange={(e) => setGlobalFilter(e.target.value)}
              className="w-full sm:w-72"
              inputProps={{ "aria-label": "Search forms" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          )}
        </div>
      )}

      {loading ? (
        <div className="app-card rounded-2xl border">
          <LoadingState label="Loading your forms…" />
        </div>
      ) : dataStore.length === 0 ? (
        <div className="app-card rounded-2xl border">
          <EmptyState
            icon={DescriptionIcon}
            title="No forms yet"
            description={
              <>
                If you think this is a mistake, please contact{" "}
                <a href="mailto:helpdesk@bigclumber.com" className="app-link">
                  Helpdesk
                </a>
                .
              </>
            }
            action={
              <Button
                component={Link}
                to="/forms/new/rfq"
                variant="contained"
                startIcon={<AddIcon />}
              >
                New RFQ Form
              </Button>
            }
          />
        </div>
      ) : data.length === 0 ? (
        <div className="app-card rounded-2xl border">
          <EmptyState
            compact
            icon={SearchOffIcon}
            title={`No ${
              statusFilter === "draft" ? "drafts" : "submitted forms"
            } to show.`}
          />
        </div>
      ) : (
        <div className="app-card overflow-hidden rounded-2xl border">
          <div className="overflow-x-auto">
            <table className="app-table" {...getTableProps()}>
              <thead>
                {headerGroups.map((headerGroup) => (
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      <th
                        {...column.getHeaderProps(
                          column.getSortByToggleProps()
                        )}
                        className={
                          column.canSort ? "cursor-pointer select-none" : ""
                        }
                      >
                        <span className="inline-flex items-center gap-1">
                          {column.render("Header")}
                          {column.isSorted &&
                            (column.isSortedDesc ? (
                              <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                            ) : (
                              <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                            ))}
                        </span>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                {page.map((row) => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()}>
                      {row.cells.map((cell) => (
                        <td
                          {...cell.getCellProps()}
                          className={
                            cell.column.id === "entry_date"
                              ? "whitespace-nowrap tabular-nums"
                              : ""
                          }
                        >
                          {cell.render("Cell")}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {pageOptions.length > 1 && (
            <div className="app-divider flex items-center justify-between gap-2 border-t px-4 py-3">
              <span className="app-muted text-sm tabular-nums">
                Page <strong>{state.pageIndex + 1}</strong> of{" "}
                {pageOptions.length}
              </span>
              <div className="flex gap-1">
                <Button
                  size="small"
                  onClick={() => previousPage()}
                  disabled={!canPreviousPage}
                >
                  Prev
                </Button>
                <Button
                  size="small"
                  onClick={() => nextPage()}
                  disabled={!canNextPage}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </PageShell>
  );
};

export default ViewEntries;
